// "You're here" job-first (James): one-shot GPS on open/foreground, nearest
// active job within ~150 m floats to the top. No background tracking, nothing
// stored server-side, silent fallback when denied/slow. Toggle:
// Profile > Notifications > "Put the job I'm at first" (default ON).
import { useEffect, useMemo, useState } from "react";

const RADIUS_M = 150;
const TIMEOUT_MS = 5000;
const MAX_AGE_MS = 120000; // re-check at most every 2 min on foreground

let _pos = null;        // { lat, lng } module cache — survives page nav, not reloads
let _posAt = 0;
let _inflight = null;

const _subs = new Set();
function _notify() { _subs.forEach((fn) => fn()); }

export function distM(a, b) {
  const toRad = (d) => (d * Math.PI) / 180;
  const R = 6371000;
  const dLat = toRad(b.lat - a.lat); const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

// One-shot position. Never throws; null on deny/timeout/off. The first request
// is preceded by a plain one-line reason (toast handled by caller pages via
// bp_geo_explained flag).
export function getPositionOnce({ force = false } = {}) {
  if (!("geolocation" in navigator)) return Promise.resolve(null);
  if (!force && _pos && Date.now() - _posAt < MAX_AGE_MS) return Promise.resolve(_pos);
  if (_inflight) return _inflight;
  _inflight = new Promise((resolve) => {
    navigator.geolocation.getCurrentPosition(
      (p) => {
        _pos = { lat: p.coords.latitude, lng: p.coords.longitude };
        _posAt = Date.now();
        _inflight = null;
        _notify();
        resolve(_pos);
      },
      () => { _inflight = null; resolve(null); }, // denied / off / slow → silent
      { timeout: TIMEOUT_MS, maximumAge: MAX_AGE_MS, enableHighAccuracy: false }
    );
  });
  return _inflight;
}

export function nearestJob(projects, pos, radiusM = RADIUS_M) {
  if (!pos) return null;
  let best = null;
  for (const p of projects || []) {
    if (p.lat == null || p.lng == null) continue;
    const d = distM({ lat: pos.lat, lng: pos.lng }, { lat: p.lat, lng: p.lng });
    if (d <= radiusM && (!best || d < best.distanceM)) best = { project: p, distanceM: d };
  }
  return best;
}

/**
 * React hook: nearest project within 150 m, re-checked on mount and on
 * foreground return (visibilitychange). enabled=false (toggle off) → null.
 * The one-line permission reason is shown once per browser (localStorage).
 */
export function useNearbyJob(projects, enabled = true) {
  const [pos, setPos] = useState(_pos);
  useEffect(() => {
    if (!enabled) return undefined;
    const sub = () => setPos(_pos);
    _subs.add(sub);
    const tick = (force) => {
      let explained = true;
      try {
        explained = localStorage.getItem("bp_geo_explained") === "1";
        if (!explained) localStorage.setItem("bp_geo_explained", "1");
      } catch { /* private mode */ }
      if (!explained) {
        // one-line reason, once — then the browser's own prompt appears
        import("sonner").then(({ toast }) => toast.info(
          document.documentElement.lang === "es"
            ? "Para que el trabajo donde estés aparezca primero."
            : "So the job you're standing at shows first."
        )).catch(() => {});
      }
      getPositionOnce({ force }).then((p) => setPos(p));
    };
    tick(false);
    const onVis = () => { if (document.visibilityState === "visible") tick(true); };
    document.addEventListener("visibilitychange", onVis);
    return () => { _subs.delete(sub); document.removeEventListener("visibilitychange", onVis); };
  }, [enabled]);
  return useMemo(() => (enabled ? nearestJob(projects, pos) : null), [projects, pos, enabled]);
}
